import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, Pause, Play, Trash2, Send, Eye, X } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FilterBar } from "@/components/shared/FilterBar";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ApiBroadcast {
  id: number;
  name: string;
  status: string;
  channel_name: string;
  channel_id: number;
  template_name: string;
  template_id: number;
  segment_name: string | null;
  segment_id: number | null;
  variable_mapping: Record<string, string>;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_count: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  progress: number;
  created_at: string;
}

interface ApiChannel {
  id: number;
  name: string;
  display_phone: string;
}

interface ApiTemplate {
  id: number;
  name: string;
  language: string;
  status: string;
  variable_count: number;
  body_text: string | null;
}

interface ApiSegment {
  id: number;
  name: string;
  cached_count: number;
}

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const selectClass = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const labelClass = "mb-1 block text-xs font-medium text-muted-foreground";

const VARIABLE_FIELDS = [
  { value: "name", label: "Contact name" },
  { value: "phone", label: "Phone number" },
  { value: "email", label: "Email" },
];

function BroadcastWizard({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const steps = ["Name & channel", "Template", "Audience", "Schedule", "Review"];

  const [name, setName] = useState("");
  const [channelId, setChannelId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [variableMapping, setVariableMapping] = useState<Record<string, string>>({});
  const [scheduleType, setScheduleType] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt] = useState("");

  const { data: channelsData } = useQuery({
    queryKey: ["channels"],
    queryFn: () => apiFetch<{ channels: ApiChannel[] }>("/api/channels"),
  });

  const { data: templatesData } = useQuery({
    queryKey: ["templates"],
    queryFn: () => apiFetch<{ templates: ApiTemplate[] }>("/api/templates"),
  });

  const { data: segmentsData } = useQuery({
    queryKey: ["segments"],
    queryFn: () => apiFetch<{ segments: ApiSegment[] }>("/api/segments"),
  });

  const channels = channelsData?.channels ?? [];
  const templates = (templatesData?.templates ?? []).filter((t: ApiTemplate) => t.status === "approved");
  const segments = segmentsData?.segments ?? [];
  const selectedTemplate = templates.find((t: ApiTemplate) => String(t.id) === String(templateId));
  const selectedSegment = segments.find((s: ApiSegment) => String(s.id) === String(segmentId));

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiFetch<{ broadcast: ApiBroadcast }>("/api/broadcasts", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      if (scheduleType === "now") {
        sendMutation.mutate(data.broadcast.id);
      } else {
        sendMutation.mutate(data.broadcast.id);
      }
    },
    onError: (err: Error) => alert(err.message),
  });

  const sendMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/broadcasts/${id}/send_broadcast`, {
        method: "POST",
        body: JSON.stringify(scheduleType === "later" ? { scheduled_at: scheduledAt } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["broadcasts"] });
      onClose();
    },
    onError: (err: Error) => alert(err.message),
  });

  const handleSubmit = () => {
    createMutation.mutate({
      name,
      channel_id: channelId,
      template_id: templateId,
      segment_id: segmentId || null,
      variable_mapping: variableMapping,
    });
  };

  const isPending = createMutation.isPending || sendMutation.isPending;

  const step1Valid = name.trim() && channelId;
  const step2Valid = templateId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-3xl overflow-hidden rounded-xl border border-border bg-card shadow-2xl" style={{ maxHeight: "90vh" }}>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-base font-semibold">Create broadcast</h3>
          <div className="mt-3 flex items-center gap-1">
            {steps.map((s, i) => (
              <div key={s} className="flex flex-1 items-center gap-2">
                <div className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                  step >= i + 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>{i + 1}</div>
                <span className={cn("hidden text-xs sm:inline", step >= i + 1 ? "font-medium" : "text-muted-foreground")}>{s}</span>
                {i < steps.length - 1 && <div className="h-px flex-1 bg-border" />}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-6 text-sm" style={{ minHeight: 260 }}>
          {step === 1 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Broadcast name <span className="text-destructive">*</span></label>
                <input className={inputClass} placeholder="e.g. December Newsletter" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Channel <span className="text-destructive">*</span></label>
                <select className={selectClass} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                  <option value="">Select channel...</option>
                  {channels.map((ch: ApiChannel) => (
                    <option key={ch.id} value={ch.id}>{ch.name} ({ch.display_phone})</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <label className={labelClass}>Approved template <span className="text-destructive">*</span></label>
              <select className={selectClass} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Select template...</option>
                {templates.map((t: ApiTemplate) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
                ))}
              </select>
              {selectedTemplate && selectedTemplate.body_text && (
                <p className="mt-2 rounded-md bg-muted/30 p-3 text-xs text-muted-foreground">{selectedTemplate.body_text}</p>
              )}
              {selectedTemplate && selectedTemplate.variable_count > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-medium mb-2">Map variables to contact fields</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Array.from({ length: selectedTemplate.variable_count }, (_, i) => (
                      <div key={i}>
                        <label className={labelClass}>{`{{${i + 1}}}`}</label>
                        <select
                          className={selectClass}
                          value={variableMapping[String(i + 1)] || ""}
                          onChange={(e) => setVariableMapping({ ...variableMapping, [String(i + 1)]: e.target.value })}
                        >
                          <option value="">Select field...</option>
                          {VARIABLE_FIELDS.map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <label className={labelClass}>Audience</label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {segments.map((seg: ApiSegment) => (
                  <div
                    key={seg.id}
                    onClick={() => setSegmentId(String(seg.id))}
                    className={cn(
                      "cursor-pointer rounded-md border p-4",
                      String(segmentId) === String(seg.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                    )}
                  >
                    <div className="font-medium text-sm">{seg.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{seg.cached_count} contacts</div>
                  </div>
                ))}
                {segments.length === 0 && (
                  <div className="col-span-2 rounded-md border border-border p-6 text-center text-sm text-muted-foreground">
                    No segments yet. Create one in the Audience page first.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-2 gap-4">
              <div
                onClick={() => setScheduleType("now")}
                className={cn("cursor-pointer rounded-md border p-4 text-sm",
                  scheduleType === "now" ? "border-primary bg-primary/5 font-medium" : "border-border")}
              >
                <Send className="h-5 w-5 mb-2 text-primary" />
                Send now
              </div>
              <div
                onClick={() => setScheduleType("later")}
                className={cn("cursor-pointer rounded-md border p-4 text-sm",
                  scheduleType === "later" ? "border-primary bg-primary/5" : "border-border")}
              >
                <div className="font-medium">Schedule</div>
                {scheduleType === "later" && (
                  <input
                    type="datetime-local"
                    className="mt-2 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                )}
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <dl className="grid grid-cols-2 gap-y-2 rounded-md border border-border bg-muted/30 p-4 text-sm">
                <dt className="text-muted-foreground">Name</dt><dd className="font-medium">{name}</dd>
                <dt className="text-muted-foreground">Channel</dt><dd>{channels.find((c: ApiChannel) => String(c.id) === channelId)?.name}</dd>
                <dt className="text-muted-foreground">Template</dt><dd className="font-mono">{selectedTemplate?.name}</dd>
                <dt className="text-muted-foreground">Audience</dt><dd>{selectedSegment?.name || "All opted-in"} ({selectedSegment?.cached_count ?? "?"})</dd>
                <dt className="text-muted-foreground">Schedule</dt><dd>{scheduleType === "now" ? "Send now" : scheduledAt || "Not set"}</dd>
                {Object.keys(variableMapping).length > 0 && (
                  <>
                    <dt className="text-muted-foreground">Variables</dt>
                    <dd>{Object.entries(variableMapping).map(([k, v]) => `{{${k}}} → ${v}`).join(", ")}</dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-6 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={step === 1} onClick={() => setStep(step - 1)}>Back</Button>
            {step < 5 ? (
              <Button
                size="sm"
                disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
                onClick={() => setStep(step + 1)}
              >
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={handleSubmit} disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {scheduleType === "now" ? "Send broadcast" : "Schedule broadcast"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BroadcastDetailDrawer({
  broadcast,
  onClose,
}: {
  broadcast: ApiBroadcast;
  onClose: () => void;
}) {
  const pct = broadcast.total_count ? Math.round((broadcast.sent_count / broadcast.total_count) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/30" />
      <div onClick={(e) => e.stopPropagation()} className="relative h-full w-full max-w-md overflow-y-auto bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h3 className="font-semibold">{broadcast.name}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-5 p-6">
          <StatusBadge status={broadcast.status} />

          <div className="rounded-md border border-border divide-y divide-border text-xs">
            <div className="flex justify-between px-3 py-2"><span className="text-muted-foreground">Channel</span><span className="font-medium">{broadcast.channel_name}</span></div>
            <div className="flex justify-between px-3 py-2"><span className="text-muted-foreground">Template</span><span className="font-medium font-mono">{broadcast.template_name}</span></div>
            <div className="flex justify-between px-3 py-2"><span className="text-muted-foreground">Audience</span><span className="font-medium">{broadcast.segment_name || "Manual"}</span></div>
            <div className="flex justify-between px-3 py-2"><span className="text-muted-foreground">Created</span><span className="font-medium">{new Date(broadcast.created_at).toLocaleString()}</span></div>
            {broadcast.started_at && <div className="flex justify-between px-3 py-2"><span className="text-muted-foreground">Started</span><span className="font-medium">{new Date(broadcast.started_at).toLocaleString()}</span></div>}
            {broadcast.completed_at && <div className="flex justify-between px-3 py-2"><span className="text-muted-foreground">Completed</span><span className="font-medium">{new Date(broadcast.completed_at).toLocaleString()}</span></div>}
          </div>

          {broadcast.total_count > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Delivery progress</h4>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs font-medium">{pct}%</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-border p-3 text-center">
                  <div className="text-lg font-semibold">{broadcast.total_count}</div>
                  <div className="text-[10px] text-muted-foreground">Total</div>
                </div>
                <div className="rounded-md border border-border p-3 text-center">
                  <div className="text-lg font-semibold text-green-600">{broadcast.sent_count}</div>
                  <div className="text-[10px] text-muted-foreground">Sent</div>
                </div>
                <div className="rounded-md border border-border p-3 text-center">
                  <div className="text-lg font-semibold text-blue-600">{broadcast.delivered_count}</div>
                  <div className="text-[10px] text-muted-foreground">Delivered</div>
                </div>
                <div className="rounded-md border border-border p-3 text-center">
                  <div className="text-lg font-semibold text-red-600">{broadcast.failed_count}</div>
                  <div className="text-[10px] text-muted-foreground">Failed</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Broadcasts() {
  const queryClient = useQueryClient();
  const [wizard, setWizard] = useState(false);
  const [viewBroadcast, setViewBroadcast] = useState<ApiBroadcast | null>(null);

  const { data } = useQuery({
    queryKey: ["broadcasts"],
    queryFn: () => apiFetch<{ broadcasts: ApiBroadcast[]; counts: Record<string, number> }>("/api/broadcasts"),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/broadcasts/${id}/pause`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["broadcasts"] }),
  });

  const resumeMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/broadcasts/${id}/resume`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["broadcasts"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/broadcasts/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["broadcasts"] }),
  });

  const broadcasts = data?.broadcasts ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Broadcasts"
        description="Send approved templates to many contacts at once."
        actions={
          <Button size="sm" onClick={() => setWizard(true)}>
            <Plus className="h-4 w-4" /> Create broadcast
          </Button>
        }
      />

      <FilterBar searchPlaceholder="Search broadcasts..." />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Channel</th>
              <th className="px-4 py-3 text-left font-medium">Template</th>
              <th className="px-4 py-3 text-left font-medium">Progress</th>
              <th className="px-4 py-3 text-left font-medium">Created</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {broadcasts.map((b) => {
              const pct = b.total_count ? Math.round(((b.sent_count + b.failed_count) / b.total_count) * 100) : 0;
              return (
                <tr key={b.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => setViewBroadcast(b)}>
                  <td className="px-4 py-3 font-medium">{b.name}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-3 text-xs">{b.channel_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{b.template_name}</td>
                  <td className="w-48 px-4 py-3">
                    {b.total_count > 0 ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-10 text-right text-xs text-muted-foreground">{pct}%</span>
                        </div>
                        <div className="mt-1 flex gap-3 text-[10px] text-muted-foreground">
                          <span className="text-green-600">{b.sent_count} sent</span>
                          <span className="text-red-600">{b.failed_count} failed</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</td>
                  <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setViewBroadcast(b)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {b.status === "sending" && (
                        <Button variant="ghost" size="icon" onClick={() => pauseMutation.mutate(b.id)}>
                          <Pause className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {b.status === "paused" && (
                        <Button variant="ghost" size="icon" onClick={() => resumeMutation.mutate(b.id)}>
                          <Play className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {["draft", "scheduled", "failed"].includes(b.status) && (
                        <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete "${b.name}"?`)) deleteMutation.mutate(b.id); }}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {broadcasts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No broadcasts yet. Create your first one to start reaching your audience.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {wizard && <BroadcastWizard onClose={() => setWizard(false)} />}
      {viewBroadcast && <BroadcastDetailDrawer broadcast={viewBroadcast} onClose={() => setViewBroadcast(null)} />}
    </div>
  );
}
